import Component from '@bythepixel/component-loader';

/* global YT */

/**
 * Fraction of the block that must be in view before the video starts. Tune
 * alongside ROOT_MARGIN if the trigger point feels too early/late — together
 * they make playback kick in roughly a third to a half of the way into the
 * block.
 */
const PLAY_THRESHOLD = 0.5;
const ROOT_MARGIN = '0px 0px -25% 0px';

const PLAYER_VARS = {
  autoplay: 1,
  mute: 1, // required for browsers to allow scroll-triggered (no-gesture) autoplay
  controls: 1, // native controls so the viewer can unmute / scrub
  rel: 0, // don't surface unrelated channels in the end screen
  modestbranding: 1,
  playsinline: 1, // iOS: play in place instead of forcing fullscreen
};

const CSS_CLASSES = {
  // Block-agnostic: shared by the large and small video-highlight blocks.
  playing: 'landing-video--is-playing',
};

/**
 * Video-highlight block (large and small variants). Lazily builds a YouTube
 * IFrame player on first scroll-into-view, autoplays it muted, and pauses/
 * resumes it as the block leaves/re-enters the viewport. Clicking the poster
 * also starts it.
 */
export default class LandingVideo extends Component {
  static selector = '[comp-landing-video]';

  #videoId = null;
  #player = null;
  #playerHost = null;
  #poster = null;
  #observer = null;
  #isApiReady = false;
  #pendingPlay = false;

  constructor(...args) {
    super(...args);
    this.init();
  }

  init() {
    this.#videoId = this.$container.dataset.videoId;

    if (!this.#videoId) {
      return;
    }

    this.#playerHost = this.$container.querySelector('[data-video-player]');
    this.#poster = this.$container.querySelector('[data-video-poster]');

    if (!this.#playerHost) {
      return;
    }

    if (this.#poster) {
      this.#poster.addEventListener('click', () => this.activate());
    }

    this.loadYouTubeAPI();
    this.observe();
  }

  /**
   * Load the YouTube IFrame API once per page, queueing our ready-callback
   * behind any other component that's already loading it.
   */
  loadYouTubeAPI() {
    if (window.YT && window.YT.Player) {
      this.#isApiReady = true;
      return;
    }

    if (window.onYouTubeIframeAPIReady) {
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        previous();
        this.onApiReady();
      };
      return;
    }

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(tag, firstScript);

    window.onYouTubeIframeAPIReady = () => this.onApiReady();
  }

  onApiReady() {
    this.#isApiReady = true;

    // The block may have already scrolled into view (or been clicked) before
    // the API finished loading — honour that deferred intent now.
    if (this.#pendingPlay) {
      this.#pendingPlay = false;
      this.createPlayer();
    }
  }

  /**
   * Watch the block: start (or resume) when sufficiently in view, pause when
   * it leaves.
   */
  observe() {
    if (!('IntersectionObserver' in window)) {
      return; // poster click still works as a fallback
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const inView = entry.isIntersecting && entry.intersectionRatio >= PLAY_THRESHOLD;

          if (inView) {
            this.activate();
          } else if (this.#player) {
            this.#player.pauseVideo();
          }
        });
      },
      { threshold: [0, PLAY_THRESHOLD], rootMargin: ROOT_MARGIN },
    );

    this.#observer.observe(this.$container);
  }

  /**
   * Build the player on first call, otherwise just resume it.
   */
  activate() {
    if (this.#player) {
      this.#player.playVideo();
      return;
    }

    if (!this.#isApiReady) {
      this.#pendingPlay = true;
      return;
    }

    this.createPlayer();
  }

  createPlayer() {
    if (this.#player) {
      return;
    }

    this.#player = new YT.Player(this.#playerHost, {
      videoId: this.#videoId,
      playerVars: PLAYER_VARS,
      events: {
        onReady: (event) => event.target.playVideo(),
        onStateChange: (event) => {
          if (event.data === YT.PlayerState.PLAYING) {
            this.$container.classList.add(CSS_CLASSES.playing);
          }
        },
      },
    });
  }

  destroy() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }

    if (this.#player) {
      this.#player.destroy();
      this.#player = null;
    }
  }
}
